import type { Permission, Role } from "@/utils/permissions.data";
import { permissionToRoles, roleToPermissions } from "@/utils/permissions.data";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { RoleAssignment } from "gitpod-next-api/gitpod/v1/group_pb";
import { type ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";

export type PlainRoleAssignment = PlainMessage<RoleAssignment>;

/**
 * Resource permissions with resolved group IDs and permissions.
 */
export type ResourcePermissions = {
    resourceId: string;
    resourceType: ResourceType;
    permissions: ReadonlySet<Permission>;
    groupIds: string[];
};

/**
 * Result of computing user resource permissions from role assignments.
 */
export type ComputedResourcePermissions = {
    /** Map of (resourceType, resourceId) -> resource permissions */
    data: Map<ResourceType, Map<string, ResourcePermissions>>;
    /** Helper to get permissions for a specific resource */
    groupPermissions: (resourceType: ResourceType, resourceId: string) => ReadonlySet<Permission>;
};

/**
 * Check if any of the given roles has a specific permission.
 * Uses optimized O(n) lookup via roleToPermissions index.
 * @param roles - Array of ResourceRole enum values
 * @param permission - The permission to check for
 * @returns true if at least one role has the permission, false otherwise
 */
export function rolesHasPermission(roles: Role[], permission: Permission): boolean {
    return roles.some((role) => roleToPermissions[role]?.has(permission));
}

/**
 * Computes resource permissions from role assignments.
 * Resolves roles to permissions and organizes data by resource.
 *
 * @param assignments - List of role assignments
 * @returns Structured permission data with O(1) lookups by (resourceType, resourceId)
 */
export function computeResourcePermissions(assignments: PlainRoleAssignment[]): ComputedResourcePermissions {
    const data = new Map<ResourceType, Map<string, ResourcePermissions>>();

    for (const assignment of assignments) {
        const { resourceId, resourceType, resourceRole, groupId } = assignment;

        // Get or create resource type map
        if (!data.has(resourceType)) {
            data.set(resourceType, new Map());
        }
        const resourceMap = data.get(resourceType)!;

        // Get or create resource permissions entry for this (resourceType, resourceId) pair
        let resourcePerms = resourceMap.get(resourceId);
        if (!resourcePerms) {
            resourcePerms = {
                resourceId,
                resourceType,
                permissions: new Set<Permission>(),
                groupIds: [],
            };
            resourceMap.set(resourceId, resourcePerms);
        }

        // Add group ID if not already present
        if (!resourcePerms.groupIds.includes(groupId)) {
            resourcePerms.groupIds.push(groupId);
        }

        // Resolve role to permissions and add them
        const rolePermissions = roleToPermissions[resourceRole];
        if (rolePermissions) {
            // Merge permissions into the set (Set automatically handles duplicates)
            for (const permission of rolePermissions) {
                (resourcePerms.permissions as Set<Permission>).add(permission);
            }
        }
    }

    return {
        data,
        groupPermissions: (resourceType: ResourceType, resourceId: string) => {
            return data.get(resourceType)?.get(resourceId)?.permissions ?? new Set<Permission>();
        },
    };
}

// Re-export types and data for convenience
export type { Permission, Role };
// Both mappings use Set for O(1) lookups
export { permissionToRoles, roleToPermissions };
